import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { ResumeDatabaseBanner } from 'components/Banners/ResumeDatabaseBanner';
import { SalarySurveyBanner } from 'components/Banners/SalarySurveyBanner';
import { ClientOnly } from 'containers/ClientOnly';
import { setAdTypeOnPageLoad } from 'modules/main/store/ducks/layout';
import { useMainDispatch } from 'modules/main/store/hooks/useMainDispatch';
import { useMainSelector } from 'modules/main/store/hooks/useMainSelector';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import {
  NOTIFICATIONS,
  NotificationStatus,
  useLocalNotifications,
  useUpdateLocalNotification,
} from 'utils/localNotifications';
import { promoteResumeDatabase } from 'utils/resumeDatabase';
import { promoteSalarySurvey } from 'utils/salarySurvey';

import { BannerAdsBox } from './BannerAds.styled';

export function BannerAds() {
  const dispatch = useMainDispatch();

  const adTypeOnPageLoad = useMainSelector(
    (state) => state.layout.adTypeOnPageLoad,
  );

  const userEnvironment = useUserEnvironment();
  const { isUserLoaded, isEnvironmentLoaded } = userEnvironment;
  const location = useLocation();

  const { localNotifications, isReady, isSet } = useLocalNotifications();

  const updateLocalNotification = useUpdateLocalNotification();

  const salarySurveyNotification = useMemo(
    () =>
      localNotifications?.find(
        (notification) => notification.type === NOTIFICATIONS.SALARY_SURVEY_CTA,
      ),

    [localNotifications],
  );

  const resumeDatabaseNotification = useMemo(
    () =>
      localNotifications?.find(
        (notification) => notification.type === NOTIFICATIONS.UPLOAD_RESUME,
      ),

    [localNotifications],
  );

  // Set initial status for the notifications
  useEffect(() => {
    if (isReady && !isSet(NOTIFICATIONS.UPLOAD_RESUME)) {
      updateLocalNotification(NOTIFICATIONS.UPLOAD_RESUME, {
        status: NotificationStatus.NEW,
      });
    }

    if (isReady && !isSet(NOTIFICATIONS.SALARY_SURVEY_CTA)) {
      updateLocalNotification(NOTIFICATIONS.SALARY_SURVEY_CTA, {
        status: NotificationStatus.NEW,
      });
    }
  }, [isReady, localNotifications, isSet, updateLocalNotification]);

  const showSalarySurveyBanner =
    !location.pathname.includes('/salaries') &&
    promoteSalarySurvey({
      userEnvironment,
    }) &&
    salarySurveyNotification?.status === NotificationStatus.NEW;

  useEffect(() => {
    if (
      isUserLoaded &&
      isEnvironmentLoaded &&
      adTypeOnPageLoad !== 'SALARY_SURVEY' &&
      showSalarySurveyBanner
    ) {
      dispatch(setAdTypeOnPageLoad('SALARY_SURVEY'));
    }
  }, [
    isUserLoaded,
    isEnvironmentLoaded,
    dispatch,
    adTypeOnPageLoad,
    showSalarySurveyBanner,
  ]);

  const showResumeDatabaseBanner =
    !showSalarySurveyBanner &&
    // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
    !['GRAD_FAIRS', 'SALARY_SURVEY'].includes(adTypeOnPageLoad) &&
    promoteResumeDatabase({
      userEnvironment,
      allowLoggedOut: true,
    }) &&
    resumeDatabaseNotification?.status === NotificationStatus.NEW;

  useEffect(() => {
    if (
      isUserLoaded &&
      isEnvironmentLoaded &&
      adTypeOnPageLoad !== 'RESUME_DB' &&
      showResumeDatabaseBanner
    ) {
      dispatch(setAdTypeOnPageLoad('RESUME_DB'));
    }
  }, [
    isUserLoaded,
    isEnvironmentLoaded,
    adTypeOnPageLoad,
    showResumeDatabaseBanner,
    dispatch,
  ]);

  return (
    <BannerAdsBox
      data-qa-id="ads-container"
      mt={[60, null, null, 0]}
      mb={[-60, null, null, 0]}
    >
      <ClientOnly>
        {showResumeDatabaseBanner ? <ResumeDatabaseBanner /> : null}
        {showSalarySurveyBanner ? <SalarySurveyBanner /> : null}
      </ClientOnly>
    </BannerAdsBox>
  );
}
